import { useMemo } from 'react';
import RewardIcon from '../../assets/logo/RewardIcon';
import { RewardInterface } from '../../services/exports/Interfaces';
import DiscountCircleLabel from './DiscountCircleLabel';
import collect from 'collect.js';
import { useTranslation } from 'react-i18next';
import useRewardAccount from '../../hooks/loyalty/useRewardAccount';

interface Props {
  hideTitle?: boolean;
  hideDescription?: boolean;
}

const RewardStatus = ({ hideTitle, hideDescription }: Props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Rewards:RewardStatus' });

  const { balance, rewards, availableReward, nextReward, maxReward, maxRewardProgress } = useRewardAccount();

  const renderExplanation = useMemo(() => {
    const getDescription = () => {
      if (!availableReward || maxReward?.is_available) {
        return null;
      }

      return t('description', {
        points: balance,
      });
    };

    return (
      <div>
        {!hideTitle && (
          <h6 className="mb-sm">
            {availableReward ? t('titles.redeem', { discount: availableReward.discount }) : t('titles.earn')}
          </h6>
        )}
        {getDescription() && !hideDescription && (
          <p className="mb-sm text-brand-text-secondary text-sm">{getDescription()}</p>
        )}
      </div>
    );
  }, [availableReward, maxReward, balance, hideTitle, hideDescription]);

  const renderPointsOverLay = useMemo(() => {
    const isActive = (reward: RewardInterface) =>
      reward.cost >= balance && (!nextReward || reward.cost < nextReward.cost);

    return (
      <div className={pointsContainer}>
        {rewards.map((item) => (
          <div className={pointsWrapper} key={`reward-${item.id}`}>
            <div className={point(isActive(item))} />
            <div className="absolute top-6">
              <p className="text-xs text-brand-text-secondary">{item.cost}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }, [rewards, balance]);

  return (
    <div>
      {renderExplanation}
      <div className={progressBarParentContainer}>
        <div className={totalPointsContainer}>
          <RewardIcon fill className="mr-xs" />
          <span>
            <h3 className="inline">{balance}</h3> <h6 className="inline">{t('point', { count: balance })}</h6>
          </span>
        </div>
        <div className="relative">
          <div className={progressBarContainer}>
            <div className="h-full rounded-xs bg-brand-primary" style={{ width: maxRewardProgress + '%' }} />
          </div>
          {renderPointsOverLay}
        </div>
      </div>
      <div className={progressCircleRow}>
        {rewards.map((item) => {
          const searchReverseIndex = collect(rewards)
            .reverse()
            .toArray()
            .findIndex((reward: RewardInterface) => item.id === reward.id);

          return (
            <div
              className={progressCircleContainer(rewards?.length ?? 0)}
              key={`reward-circle-${item.id}`}
              style={{
                marginRight: (searchReverseIndex !== -1 ? searchReverseIndex : 0) * -4,
              }}
            >
              <DiscountCircleLabel data={item} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RewardStatus;

const progressBarParentContainer = 'mb-[40px]';

const progressBarContainer = 'h-[12px] w-full rounded bg-gray-100 relative overflow-hidden';

const pointsContainer = 'h-[12px] w-full absolute top-0 flex flex-row justify-between items-center px-xs rounded';

const pointsWrapper = 'h-full flex flex-col mx-2xs justify-center items-center relative';

const point = (active: boolean) => {
  return `${active ? 'h-[16px] w-[12px]' : 'h-[5px] w-[5px]'} rounded-full bg-white`;
};

const totalPointsContainer = 'flex flex-row items-center mb-xs';

const progressCircleRow = 'flex flex-row items-end justify-end mt-lg';

const progressCircleContainer = (length: number) => `flex justify-end w-1/${length}`;
